/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { WorkCreateProjectDto } from '../models/work-create-project-dto';
import { WorkProject } from '../models/work-project';
import { WorkSelfServiceProjectAsRequestedDto } from '../models/work-self-service-project-as-requested-dto';
import { WorkSelfserviceAppointmentBlockedDayDto } from '../models/work-selfservice-appointment-blocked-day-dto';
import { WorkSelfserviceProjectsPagedDto } from '../models/work-selfservice-projects-paged-dto';
import { WorkSuccessDto } from '../models/work-success-dto';
import { WorkUpdateProjectDto } from '../models/work-update-project-dto';

@Injectable({ providedIn: 'root' })
export class WorkSelfserviceProjectsService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `selfserviceProjectsV2ControllerGetAllProjects()` */
  static readonly SelfserviceProjectsV2ControllerGetAllProjectsPath = '/v2/selfservice/projects';

  /**
   * Get all projects for the authenticated contact.
   *
   * Returns paginated projects belonging to the authenticated contact
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `selfserviceProjectsV2ControllerGetAllProjects()` instead.
   *
   * This method doesn't expect any request body.
   */
  selfserviceProjectsV2ControllerGetAllProjects$Response(
    params?: {

    /**
     * withDocuments
     */
      withDocuments?: boolean;

    /**
     * Page number (default: 1)
     */
      page?: number;

    /**
     * Items per page (default: 10)
     */
      limit?: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkSelfserviceProjectsPagedDto>> {
    const rb = new RequestBuilder(this.rootUrl, WorkSelfserviceProjectsService.SelfserviceProjectsV2ControllerGetAllProjectsPath, 'get');
    if (params) {
      rb.query('withDocuments', params.withDocuments, {});
      rb.query('page', params.page, {});
      rb.query('limit', params.limit, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkSelfserviceProjectsPagedDto>;
      })
    );
  }

  /**
   * Get all projects for the authenticated contact.
   *
   * Returns paginated projects belonging to the authenticated contact
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `selfserviceProjectsV2ControllerGetAllProjects$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  selfserviceProjectsV2ControllerGetAllProjects(
    params?: {

    /**
     * withDocuments
     */
      withDocuments?: boolean;

    /**
     * Page number (default: 1)
     */
      page?: number;

    /**
     * Items per page (default: 10)
     */
      limit?: number;
    },
    context?: HttpContext
  ): Observable<WorkSelfserviceProjectsPagedDto> {
    return this.selfserviceProjectsV2ControllerGetAllProjects$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkSelfserviceProjectsPagedDto>): WorkSelfserviceProjectsPagedDto => r.body)
    );
  }

  /** Path part for operation `selfserviceProjectsV2ControllerCreateProject()` */
  static readonly SelfserviceProjectsV2ControllerCreateProjectPath = '/v2/selfservice/projects';

  /**
   * Create a new project.
   *
   * Creates a new project for the authenticated contact using the v2 projects service business logic
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `selfserviceProjectsV2ControllerCreateProject()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  selfserviceProjectsV2ControllerCreateProject$Response(
    params: {
      body: WorkCreateProjectDto
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkProject>> {
    const rb = new RequestBuilder(this.rootUrl, WorkSelfserviceProjectsService.SelfserviceProjectsV2ControllerCreateProjectPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkProject>;
      })
    );
  }

  /**
   * Create a new project.
   *
   * Creates a new project for the authenticated contact using the v2 projects service business logic
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `selfserviceProjectsV2ControllerCreateProject$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  selfserviceProjectsV2ControllerCreateProject(
    params: {
      body: WorkCreateProjectDto
    },
    context?: HttpContext
  ): Observable<WorkProject> {
    return this.selfserviceProjectsV2ControllerCreateProject$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkProject>): WorkProject => r.body)
    );
  }

  /** Path part for operation `selfserviceProjectsV2ControllerSearchProjects()` */
  static readonly SelfserviceProjectsV2ControllerSearchProjectsPath = '/v2/selfservice/projects/search';

  /**
   * Search projects by multiple fields.
   *
   * Search for projects by numberQualified, objectStreet, objectCity, or objectZip. Returns all projects if search term is empty.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `selfserviceProjectsV2ControllerSearchProjects()` instead.
   *
   * This method doesn't expect any request body.
   */
  selfserviceProjectsV2ControllerSearchProjects$Response(
    params?: {

    /**
     * Search term (searches in numberQualified, objectStreet, objectCity, objectZip)
     */
      q?: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Array<WorkProject>>> {
    const rb = new RequestBuilder(this.rootUrl, WorkSelfserviceProjectsService.SelfserviceProjectsV2ControllerSearchProjectsPath, 'get');
    if (params) {
      rb.query('q', params.q, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<WorkProject>>;
      })
    );
  }

  /**
   * Search projects by multiple fields.
   *
   * Search for projects by numberQualified, objectStreet, objectCity, or objectZip. Returns all projects if search term is empty.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `selfserviceProjectsV2ControllerSearchProjects$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  selfserviceProjectsV2ControllerSearchProjects(
    params?: {

    /**
     * Search term (searches in numberQualified, objectStreet, objectCity, objectZip)
     */
      q?: string;
    },
    context?: HttpContext
  ): Observable<Array<WorkProject>> {
    return this.selfserviceProjectsV2ControllerSearchProjects$Response(params, context).pipe(
      map((r: StrictHttpResponse<Array<WorkProject>>): Array<WorkProject> => r.body)
    );
  }

  /** Path part for operation `selfserviceProjectsV2ControllerCheckHasProjects()` */
  static readonly SelfserviceProjectsV2ControllerCheckHasProjectsPath = '/v2/selfservice/projects/hasprojects';

  /**
   * Check if a contact has any projects.
   *
   * Returns a boolean
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `selfserviceProjectsV2ControllerCheckHasProjects()` instead.
   *
   * This method doesn't expect any request body.
   */
  selfserviceProjectsV2ControllerCheckHasProjects$Response(
    params?: {
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkSuccessDto>> {
    const rb = new RequestBuilder(this.rootUrl, WorkSelfserviceProjectsService.SelfserviceProjectsV2ControllerCheckHasProjectsPath, 'get');
    if (params) {
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkSuccessDto>;
      })
    );
  }

  /**
   * Check if a contact has any projects.
   *
   * Returns a boolean
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `selfserviceProjectsV2ControllerCheckHasProjects$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  selfserviceProjectsV2ControllerCheckHasProjects(
    params?: {
    },
    context?: HttpContext
  ): Observable<WorkSuccessDto> {
    return this.selfserviceProjectsV2ControllerCheckHasProjects$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkSuccessDto>): WorkSuccessDto => r.body)
    );
  }

  /** Path part for operation `selfserviceProjectsV2ControllerGetBlockedDays()` */
  static readonly SelfserviceProjectsV2ControllerGetBlockedDaysPath = '/v2/selfservice/projects/blockeddays';

  /**
   * Get the blocked days for calendar-appointments.
   *
   * Returns list of days
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `selfserviceProjectsV2ControllerGetBlockedDays()` instead.
   *
   * This method doesn't expect any request body.
   */
  selfserviceProjectsV2ControllerGetBlockedDays$Response(
    params?: {
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Array<WorkSelfserviceAppointmentBlockedDayDto>>> {
    const rb = new RequestBuilder(this.rootUrl, WorkSelfserviceProjectsService.SelfserviceProjectsV2ControllerGetBlockedDaysPath, 'get');
    if (params) {
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<WorkSelfserviceAppointmentBlockedDayDto>>;
      })
    );
  }

  /**
   * Get the blocked days for calendar-appointments.
   *
   * Returns list of days
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `selfserviceProjectsV2ControllerGetBlockedDays$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  selfserviceProjectsV2ControllerGetBlockedDays(
    params?: {
    },
    context?: HttpContext
  ): Observable<Array<WorkSelfserviceAppointmentBlockedDayDto>> {
    return this.selfserviceProjectsV2ControllerGetBlockedDays$Response(params, context).pipe(
      map((r: StrictHttpResponse<Array<WorkSelfserviceAppointmentBlockedDayDto>>): Array<WorkSelfserviceAppointmentBlockedDayDto> => r.body)
    );
  }

  /** Path part for operation `selfserviceProjectsV2ControllerGetProjectById()` */
  static readonly SelfserviceProjectsV2ControllerGetProjectByIdPath = '/v2/selfservice/projects/{id}';

  /**
   * Get a specific project by ID.
   *
   * Returns a single project with all details if it belongs to the authenticated contact
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `selfserviceProjectsV2ControllerGetProjectById()` instead.
   *
   * This method doesn't expect any request body.
   */
  selfserviceProjectsV2ControllerGetProjectById$Response(
    params: {

    /**
     * Project ID
     */
      id: number;

    /**
     * withCalculationitemGroup
     */
      withCalculationitemGroup?: boolean;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkProject>> {
    const rb = new RequestBuilder(this.rootUrl, WorkSelfserviceProjectsService.SelfserviceProjectsV2ControllerGetProjectByIdPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
      rb.query('withCalculationitemGroup', params.withCalculationitemGroup, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkProject>;
      })
    );
  }

  /**
   * Get a specific project by ID.
   *
   * Returns a single project with all details if it belongs to the authenticated contact
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `selfserviceProjectsV2ControllerGetProjectById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  selfserviceProjectsV2ControllerGetProjectById(
    params: {

    /**
     * Project ID
     */
      id: number;

    /**
     * withCalculationitemGroup
     */
      withCalculationitemGroup?: boolean;
    },
    context?: HttpContext
  ): Observable<WorkProject> {
    return this.selfserviceProjectsV2ControllerGetProjectById$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkProject>): WorkProject => r.body)
    );
  }

  /** Path part for operation `selfserviceProjectsV2ControllerUpdateProjectCalculations()` */
  static readonly SelfserviceProjectsV2ControllerUpdateProjectCalculationsPath = '/v2/selfservice/projects/{id}';

  /**
   * Update calculations of a project.
   *
   * Update calculations of a project
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `selfserviceProjectsV2ControllerUpdateProjectCalculations()` instead.
   *
   * This method doesn't expect any request body.
   */
  selfserviceProjectsV2ControllerUpdateProjectCalculations$Response(
    params: {

    /**
     * Project ID
     */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkSuccessDto>> {
    const rb = new RequestBuilder(this.rootUrl, WorkSelfserviceProjectsService.SelfserviceProjectsV2ControllerUpdateProjectCalculationsPath, 'post');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkSuccessDto>;
      })
    );
  }

  /**
   * Update calculations of a project.
   *
   * Update calculations of a project
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `selfserviceProjectsV2ControllerUpdateProjectCalculations$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  selfserviceProjectsV2ControllerUpdateProjectCalculations(
    params: {

    /**
     * Project ID
     */
      id: number;
    },
    context?: HttpContext
  ): Observable<WorkSuccessDto> {
    return this.selfserviceProjectsV2ControllerUpdateProjectCalculations$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkSuccessDto>): WorkSuccessDto => r.body)
    );
  }

  /** Path part for operation `selfserviceProjectsV2ControllerUpdateProject()` */
  static readonly SelfserviceProjectsV2ControllerUpdateProjectPath = '/v2/selfservice/projects/{id}';

  /**
   * Update a project.
   *
   * Updates a project (e.g., roof type, object address) if it belongs to the authenticated contact
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `selfserviceProjectsV2ControllerUpdateProject()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  selfserviceProjectsV2ControllerUpdateProject$Response(
    params: {

    /**
     * Project ID
     */
      id: number;
      body: WorkUpdateProjectDto
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkProject>> {
    const rb = new RequestBuilder(this.rootUrl, WorkSelfserviceProjectsService.SelfserviceProjectsV2ControllerUpdateProjectPath, 'patch');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkProject>;
      })
    );
  }

  /**
   * Update a project.
   *
   * Updates a project (e.g., roof type, object address) if it belongs to the authenticated contact
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `selfserviceProjectsV2ControllerUpdateProject$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  selfserviceProjectsV2ControllerUpdateProject(
    params: {

    /**
     * Project ID
     */
      id: number;
      body: WorkUpdateProjectDto
    },
    context?: HttpContext
  ): Observable<WorkProject> {
    return this.selfserviceProjectsV2ControllerUpdateProject$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkProject>): WorkProject => r.body)
    );
  }

  /** Path part for operation `selfserviceProjectsV2ControllerSetProjectAsRequested()` */
  static readonly SelfserviceProjectsV2ControllerSetProjectAsRequestedPath = '/v2/selfservice/projects/setasrequested/{id}';

  /**
   * Set the project as requested.
   *
   * Updates a project and set it as requested
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `selfserviceProjectsV2ControllerSetProjectAsRequested()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  selfserviceProjectsV2ControllerSetProjectAsRequested$Response(
    params: {

    /**
     * Project ID
     */
      id: number;
      body: WorkSelfServiceProjectAsRequestedDto
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkSuccessDto>> {
    const rb = new RequestBuilder(this.rootUrl, WorkSelfserviceProjectsService.SelfserviceProjectsV2ControllerSetProjectAsRequestedPath, 'patch');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkSuccessDto>;
      })
    );
  }

  /**
   * Set the project as requested.
   *
   * Updates a project and set it as requested
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `selfserviceProjectsV2ControllerSetProjectAsRequested$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  selfserviceProjectsV2ControllerSetProjectAsRequested(
    params: {

    /**
     * Project ID
     */
      id: number;
      body: WorkSelfServiceProjectAsRequestedDto
    },
    context?: HttpContext
  ): Observable<WorkSuccessDto> {
    return this.selfserviceProjectsV2ControllerSetProjectAsRequested$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkSuccessDto>): WorkSuccessDto => r.body)
    );
  }

  /** Path part for operation `selfserviceProjectsV2ControllerWithdrawProjectRequest()` */
  static readonly SelfserviceProjectsV2ControllerWithdrawProjectRequestPath = '/v2/selfservice/projects/withdrawrequest/{id}';

  /**
   * Withdraw a project request.
   *
   * Withdraws a project request and sets it back to draft status (27)
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `selfserviceProjectsV2ControllerWithdrawProjectRequest()` instead.
   *
   * This method doesn't expect any request body.
   */
  selfserviceProjectsV2ControllerWithdrawProjectRequest$Response(
    params: {

    /**
     * Project ID
     */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkSuccessDto>> {
    const rb = new RequestBuilder(this.rootUrl, WorkSelfserviceProjectsService.SelfserviceProjectsV2ControllerWithdrawProjectRequestPath, 'patch');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkSuccessDto>;
      })
    );
  }

  /**
   * Withdraw a project request.
   *
   * Withdraws a project request and sets it back to draft status (27)
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `selfserviceProjectsV2ControllerWithdrawProjectRequest$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  selfserviceProjectsV2ControllerWithdrawProjectRequest(
    params: {

    /**
     * Project ID
     */
      id: number;
    },
    context?: HttpContext
  ): Observable<WorkSuccessDto> {
    return this.selfserviceProjectsV2ControllerWithdrawProjectRequest$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkSuccessDto>): WorkSuccessDto => r.body)
    );
  }

}
